import React, { useState } from 'react';
import parse from 'html-react-parser';
import CtaLink from '../Global/CtaLink';
import * as styles from './product-hero-v2.module.css';
import {
    KontentStringValue,
    KontentMedia,
    KontentRichText,
} from '../../../types/KontentProps';
import {
    optimizeKontentImageUrl,
    imagePreset,
    buildKontentSrcSet,
} from '../Global/DataUtils';

export interface ProductHeroV2Props {
    kontentItemId?: string;
    product_name: KontentStringValue | string;
    product_description: KontentStringValue | string;
    product_subtitle: KontentStringValue | string;
    cta_label: KontentStringValue | string;
    gallery_images: any[];
    feature_blocks: {
        title: KontentStringValue;
        description: KontentRichText;
    }[];
    recognition_title: KontentStringValue | string;
    recognition_stars: number;
    media_logos: any[];
    cta_location?: string;
    cta_window_action?: string;
    cta_location_anchor_point?: string;
}

const ProductHeroV2: React.FC<ProductHeroV2Props> = ({
    kontentItemId,
    product_name,
    product_description,
    product_subtitle,
    cta_label,
    gallery_images = [],
    feature_blocks = [],
    recognition_title,
    recognition_stars = 5,
    media_logos = [],
    cta_location,
    cta_window_action,
    cta_location_anchor_point,
}) => {
    // Image gallery state for desktop - track which image is currently displayed
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    // CTA target logic
    const ctaTarget = cta_window_action === 'new_window' ? '_blank' : '_self';
    // Function to handle image selection
    const handleImageSelect = (index: number) => {
        setCurrentImageIndex(index);
    };

    const renderStars = (count: number) => {
        return Array.from({ length: count }, (_, i) => (
            <span key={i} className={styles.star}>
                ★
            </span>
        ));
    };

    return (
        <div
            className={styles.productHeroWrapper}
            data-kontent-item-id={kontentItemId}
        >
            {/* Header Section */}
            <div className={styles.headerSection}>
                <h1
                    className={styles.productTitle}
                    data-kontent-element-codename="product_name"
                >
                    {typeof product_name === 'string'
                        ? product_name
                        : product_name?.value}
                </h1>
                <div className={styles.productDescription}>
                    {product_description && (
                        <p
                            className={styles.descriptionText}
                            data-kontent-element-codename="product_description"
                        >
                            {parse(
                                typeof product_description === 'string'
                                    ? product_description
                                    : product_description?.value || ''
                            )}
                        </p>
                    )}
                    {product_subtitle && (
                        <p
                            className={styles.subtitleText}
                            data-kontent-element-codename="product_subtitle"
                        >
                            {parse(
                                typeof product_subtitle === 'string'
                                    ? product_subtitle
                                    : product_subtitle?.value || ''
                            )}
                        </p>
                    )}
                </div>
                <CtaLink
                    href={cta_location}
                    target={ctaTarget}
                    visualStyle="outlineDark"
                >
                    {typeof cta_label === 'string'
                        ? cta_label
                        : cta_label?.value || 'GET PRICING'}
                </CtaLink>
            </div>

            {/* Mobile Recognition Section - between button and main image */}
            <div
                className={`${styles.recognitionSection} ${styles.mobileRecognition}`}
            >
                <h3
                    className={styles.recognitionTitle}
                    data-kontent-element-codename="recognition_title"
                >
                    {typeof recognition_title === 'string'
                        ? recognition_title
                        : recognition_title?.value}
                </h3>
                <div>{renderStars(recognition_stars)}</div>
                <div className={styles.mediaLogos}>
                    {Array.isArray(media_logos) &&
                        media_logos.map((logo, index) => (
                            <div key={index} className={styles.mediaLogo}>
                                {logo?.elements?.file?.value?.[0]?.url ? (
                                    <div data-kontent-element-codename="media_logos">
                                        <img
                                            src={optimizeKontentImageUrl(
                                                logo.elements.file.value[0].url,
                                                {
                                                    w: 400,
                                                    fm: 'webp' as const,
                                                    q: 95,
                                                }
                                            )}
                                            alt={
                                                logo.elements.file.value[0]
                                                    .name ||
                                                `Media Logo ${index + 1}`
                                            }
                                            className={styles.mediaLogoImage}
                                            width={
                                                logo.elements.file.value[0]
                                                    .width || 120
                                            }
                                            height={
                                                logo.elements.file.value[0]
                                                    .height || 40
                                            }
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    </div>
                                ) : (
                                    <span className={styles.mediaLogoText}>
                                        {logo?.elements?.file?.value?.[0]
                                            ?.name || `Logo ${index + 1}`}
                                    </span>
                                )}
                            </div>
                        ))}
                </div>
            </div>

            {/* Main Product Section */}
            <div className={styles.mainProductSection}>
                <div className={styles.imageContainer}>
                    {/* Desktop: Main image + stacked gallery thumbnails */}
                    <div className={styles.desktopGallery}>
                        {/* Main image display */}
                        <div
                            className={styles.mainImageContainer}
                            data-kontent-element-codename="gallery_images"
                        >
                            {gallery_images[currentImageIndex]?.elements?.file
                                ?.value?.[0]?.url ? (
                                <img
                                    src={optimizeKontentImageUrl(
                                        gallery_images[currentImageIndex]
                                            ?.elements.file.value[0].url,
                                        {
                                            ...imagePreset.fullWidth.desktop,
                                            q: 95,
                                        }
                                    )}
                                    srcSet={buildKontentSrcSet(
                                        gallery_images[currentImageIndex]
                                            ?.elements.file.value[0].url,
                                        imagePreset.fullWidth.desktop.w,
                                        { fm: 'webp' as const, q: 95 }
                                    )}
                                    sizes="100vw"
                                    alt={
                                        gallery_images[currentImageIndex]
                                            ?.elements.file.value[0]
                                            .description ||
                                        `Gallery Image ${currentImageIndex + 1}`
                                    }
                                    className={styles.heroImage}
                                    width={
                                        gallery_images[currentImageIndex]
                                            ?.elements.file.value[0].width ||
                                        800
                                    }
                                    height={
                                        gallery_images[currentImageIndex]
                                            ?.elements.file.value[0].height ||
                                        600
                                    }
                                    loading="eager"
                                    decoding="auto"
                                    fetchPriority="high"
                                />
                            ) : (
                                <div
                                    style={{
                                        width: '100%',
                                        height: '100%',
                                        backgroundColor: '#f0f0f0',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        color: '#666',
                                    }}
                                >
                                    Image not available
                                </div>
                            )}
                        </div>

                        {/* Gallery thumbnails stacked on the right */}
                        <div className={styles.desktopThumbnails}>
                            {gallery_images?.map((image, index) => (
                                <div
                                    key={index}
                                    className={`${styles.desktopThumbnail} ${
                                        index === currentImageIndex
                                            ? styles.activeThumbnail
                                            : ''
                                    }`}
                                    data-kontent-element-codename="gallery_images"
                                    onClick={() => handleImageSelect(index)}
                                >
                                    {image?.elements?.file?.value?.[0]?.url && (
                                        <img
                                            src={optimizeKontentImageUrl(
                                                image.elements.file.value[0]
                                                    .url,
                                                {
                                                    ...imagePreset.card.desktop,
                                                    q: 95,
                                                }
                                            )}
                                            srcSet={buildKontentSrcSet(
                                                image.elements.file.value[0]
                                                    .url,
                                                imagePreset.card.desktop.w,
                                                { fm: 'webp' as const, q: 95 }
                                            )}
                                            sizes="100px"
                                            alt={
                                                image.elements.file.value[0]
                                                    .description ||
                                                `Gallery ${index + 1}`
                                            }
                                            className={styles.thumbnailImage}
                                            width={
                                                image.elements.file.value[0]
                                                    .width || 150
                                            }
                                            height={
                                                image.elements.file.value[0]
                                                    .height || 150
                                            }
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Mobile: Hero image + thumbnails below */}
                    <div className={styles.mobileGallery}>
                        <div
                            className={styles.heroImageWrapper}
                            data-kontent-element-codename="gallery_images"
                        >
                            {gallery_images[currentImageIndex]?.elements?.file
                                ?.value?.[0]?.url && (
                                <img
                                    src={optimizeKontentImageUrl(
                                        gallery_images[currentImageIndex]
                                            .elements.file.value[0].url,
                                        { ...imagePreset.card.mobile, q: 95 }
                                    )}
                                    srcSet={buildKontentSrcSet(
                                        gallery_images[currentImageIndex]
                                            .elements.file.value[0].url,
                                        imagePreset.card.mobile.w,
                                        { fm: 'webp' as const, q: 95 }
                                    )}
                                    sizes="100vw"
                                    alt={
                                        gallery_images[currentImageIndex]
                                            .elements.file.value[0]
                                            .description || 'Product Hero'
                                    }
                                    className={styles.heroImage}
                                    width={
                                        gallery_images[currentImageIndex]
                                            .elements.file.value[0].width || 800
                                    }
                                    height={
                                        gallery_images[currentImageIndex]
                                            .elements.file.value[0].height ||
                                        600
                                    }
                                    loading="eager"
                                    decoding="auto"
                                />
                            )}
                        </div>

                        <div className={styles.galleryContainer}>
                            {gallery_images?.map((image, index) => (
                                <div
                                    key={index}
                                    className={`${styles.galleryThumbnail} ${
                                        index === currentImageIndex
                                            ? styles.activeThumbnail
                                            : ''
                                    }`}
                                    data-kontent-element-codename="gallery_images"
                                    onClick={() => handleImageSelect(index)}
                                >
                                    {image?.elements?.file?.value?.[0]?.url && (
                                        <img
                                            src={optimizeKontentImageUrl(
                                                image.elements.file.value[0]
                                                    .url,
                                                {
                                                    ...imagePreset.card.mobile,
                                                    q: 95,
                                                }
                                            )}
                                            srcSet={buildKontentSrcSet(
                                                image.elements.file.value[0]
                                                    .url,
                                                imagePreset.card.mobile.w,
                                                { fm: 'webp' as const, q: 95 }
                                            )}
                                            sizes="100px"
                                            alt={
                                                image.elements.file.value[0]
                                                    .description ||
                                                `Gallery ${index + 1}`
                                            }
                                            className={styles.thumbnailImage}
                                            width={
                                                image.elements.file.value[0]
                                                    .width || 150
                                            }
                                            height={
                                                image.elements.file.value[0]
                                                    .height || 150
                                            }
                                            loading="lazy"
                                            decoding="async"
                                        />
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Feature Blocks */}
            <div className={styles.mainProductSection}>
                <div className={styles.featureBlocksContainer}>
                    {feature_blocks.map((block, index) => (
                        <div key={index} className={styles.featureBlock}>
                            <h3
                                className={styles.featureTitle}
                                data-kontent-element-codename="title"
                            >
                                {block.title?.value}
                            </h3>
                            <div
                                className={styles.featureDescription}
                                data-kontent-element-codename="description"
                            >
                                {parse(block.description?.value || '')}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Desktop Recognition Section - at bottom under feature blocks */}
            {/* <div
                className={`${styles.recognitionSection} ${styles.desktopRecognition}`}
            >
                <h3 className={styles.recognitionTitle}>
                    {typeof recognition_title === 'string'
                        ? recognition_title
                        : recognition_title?.value}
                </h3>
                <div>{renderStars(recognition_stars)}</div>
                <div className={styles.mediaLogos}>
                    {Array.isArray(media_logos) &&
                        media_logos.map((logo, index) => (
                            <div key={index} className={styles.mediaLogo}>
                                {logo?.elements?.file?.value?.[0]?.fluid ? (
                                    <img
                                        src={
                                            logo.elements.file.value[0].fluid
                                                .src
                                        }
                                        alt={
                                            logo.elements.file.value[0].name ||
                                            `Media Logo ${index + 1}`
                                        }
                                        className={styles.mediaLogoImage}
                                    />
                                ) : (
                                    <span className={styles.mediaLogoText}>
                                        {logo?.elements?.file?.value?.[0]
                                            ?.name || `Logo ${index + 1}`}
                                    </span>
                                )}
                            </div>
                        ))}
                </div>
            </div> */}
        </div>
    );
};

export default ProductHeroV2;
